import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { Calendar, Clock, MapPin, Users, UserPlus, CheckCircle } from 'lucide-react';
import { db } from '../../firebase/config';
import { useAppContext } from '../../context/AppContext';
import { parseLocalDate } from '../../utils/calculations';
import Header from '../../components/layout/Header';
import Button from '../../components/ui/Button';

const GameInvitePage = () => {
  const { groups, userId, fetchGroup, checkinOpenSpot, updateGame } = useAppContext();
  const navigate = useNavigate();
  const { groupId, gameId } = useParams();
  const [groupInfo, setGroupInfo] = useState(null);
  const [gameInfo, setGameInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requested, setRequested] = useState(false);

  const isMember = groups.some(g => g.id === groupId);

  // Carregar grupo (one-time) + jogo (real-time listener)
  useEffect(() => {
    let loaded = false;
    fetchGroup(groupId).then(g => {
      setGroupInfo(g);
      if (!g) setLoading(false);
    });

    const unsub = onSnapshot(doc(db, 'groups', groupId, 'games', gameId), (snap) => {
      if (snap.exists()) {
        setGameInfo({ id: snap.id, groupId, ...snap.data() });
      } else {
        setGameInfo(null);
      }
      if (!loaded) {
        loaded = true;
        setLoading(false);
      }
    });

    return () => unsub();
  }, [groupId, gameId, fetchGroup]);

  if (loading) {
    return (
      <>
        <Header showBack onBack={() => navigate('/')} />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
        </div>
      </>
    );
  }

  if (!groupInfo || !gameInfo) {
    return (
      <>
        <Header showBack onBack={() => navigate('/')} />
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="text-4xl mb-4">😕</div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Jogo não encontrado</h2>
          <p className="text-slate-400 text-sm mb-6">O link pode estar errado ou o jogo foi removido.</p>
          <Button onClick={() => navigate('/')}>Voltar ao Início</Button>
        </div>
      </>
    );
  }

  const confirmed = gameInfo.confirmedIds?.length || 0;
  const max = gameInfo.maxPlayers || 0;
  const spotsLeft = max > 0 ? max - confirmed : null;
  const isAlreadyConfirmed = gameInfo.confirmedIds?.includes(userId);
  const isExternalPending = (gameInfo.externalCheckins || []).includes(userId) && !(gameInfo.confirmedExternalCheckins || []).includes(userId);
  const isExternalApproved = (gameInfo.confirmedExternalCheckins || []).includes(userId);
  const gameDate = parseLocalDate(gameInfo.date);
  const isCancelled = gameInfo.status === 'cancelled';
  const isFinished = gameInfo.status === 'finished';

  const handleConfirmPresence = async () => {
    if (isMember) {
      const currentIds = gameInfo.confirmedIds || [];
      if (!currentIds.includes(userId)) {
        await updateGame(groupId, gameId, { confirmedIds: [...currentIds, userId] });
      }
      navigate(`/games/${groupId}/${gameId}`);
    } else {
      await checkinOpenSpot(groupId, gameId);
      setRequested(true);
    }
  };

  return (
    <>
      <Header showBack onBack={() => navigate('/')} />
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-sm bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
          <div className="bg-gradient-to-br from-indigo-500 to-blue-600 p-6 text-center text-white">
            <div className="text-4xl mb-3">⚽</div>
            <h2 className="text-xl font-bold">{groupInfo.name}</h2>
            <p className="text-blue-100 text-sm mt-1">Convite para jogo</p>
          </div>
          <div className="p-6 space-y-4">
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm text-slate-600">
                <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
                <span>{gameDate.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })}</span>
              </div>
              {gameInfo.time && (
                <div className="flex items-center gap-3 text-sm text-slate-600">
                  <Clock className="w-4 h-4 text-slate-400 shrink-0" />
                  <span>{gameInfo.time}</span>
                </div>
              )}
              {groupInfo.courtName && (
                <div className="flex items-center gap-3 text-sm text-slate-600">
                  <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
                  <span>{groupInfo.courtName}</span>
                </div>
              )}
              <div className="flex items-center gap-3 text-sm text-slate-600">
                <Users className="w-4 h-4 text-slate-400 shrink-0" />
                <span>{confirmed}/{max} confirmados{spotsLeft !== null && spotsLeft > 0 && ` · ${spotsLeft} vaga${spotsLeft !== 1 ? 's' : ''}`}</span>
              </div>
            </div>

            {(isCancelled || isFinished) ? (
              <div className="text-center py-3 bg-slate-50 rounded-xl">
                <p className="text-sm text-slate-500">{isCancelled ? 'Este jogo foi cancelado.' : 'Este jogo já foi encerrado.'}</p>
              </div>
            ) : isAlreadyConfirmed || isExternalApproved ? (
              <div className="text-center py-3">
                <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                <p className="text-sm font-bold text-emerald-700">Presença confirmada!</p>
                <Button variant="secondary" className="w-full mt-3" onClick={() => navigate(isMember ? `/games/${groupId}/${gameId}` : '/')}>
                  {isMember ? 'Ver Jogo' : 'Ir para Home'}
                </Button>
              </div>
            ) : isExternalPending || requested ? (
              <div className="text-center py-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-2">
                  <Users className="w-5 h-5 text-amber-600" />
                </div>
                <p className="text-sm font-bold text-amber-700">Aguardando confirmação</p>
                <p className="text-xs text-slate-400 mt-1">O admin irá analisar sua solicitação. Esta página atualiza automaticamente.</p>
              </div>
            ) : (
              <Button className="w-full" onClick={handleConfirmPresence}>
                <UserPlus className="w-4 h-4" /> {isMember ? 'Confirmar Presença' : 'Solicitar Vaga'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default GameInvitePage;
